// Rides tab (tab 06) — chronological ride list, newest first, paginated.
//
// Reads the raw ride records (not the per-day-aggregated chart dataset): the
// list shows every ride, including multiple rides on the same day. State is fed
// from app.js via setRides(), mirroring charts.setRides() from the same
// getAllRides() result.

import {
  fmtRideDistance, fmtSpeed, fmtInt, fmtDuration,
  fmtRideDate, fmtShortTime, hasTime, sourceTag,
} from './dataProcessor.js';
import { openRideEditor, openNewRide } from './rideModal.js';

const PAGE_SIZE = 15;
const HEAD_LABELS = ['Date', 'Title', 'Distance', 'Duration', 'Avg speed', 'Source', ''];
const EMPTY_COPY = 'No rides yet. Connect a data source, import a JSON file, or add one by hand.';

let rides = [];
let page = 1;
let listEl = null;
let paginationEl = null;
let metaEl = null;

export function initRides() {
  listEl = document.getElementById('rides-list');
  paginationEl = document.getElementById('rides-pagination');
  metaEl = document.getElementById('rides-meta');
  document.getElementById('rides-new')?.addEventListener('click', () => openNewRide());
}

export function setRides(all) {
  rides = sortNewestFirst(all ?? []);
  page = 1;
  render();
}

// Called when the Rides tab becomes active — re-render in case the section was
// display:none at the time setRides() ran.
export function showRides() {
  render();
}

// ── pure helpers (unit-tested) ──────────────────────────────────────────────

// Newest first. Date strings (YYYY-MM-DD or full ISO) sort lexically =
// chronologically; id is the stable tiebreak for same-instant records.
export function sortNewestFirst(all) {
  return [...all].sort((a, b) => {
    const da = String(a.date);
    const db = String(b.date);
    if (da !== db) return da < db ? 1 : -1;
    const ia = String(a.id);
    const ib = String(b.id);
    if (ia === ib) return 0;
    return ia < ib ? 1 : -1;
  });
}

export function totalPages(count) {
  return Math.max(1, Math.ceil(count / PAGE_SIZE));
}

// Page-number window with 'ellipsis' tokens, e.g. [1,2,3,'ellipsis',24].
export function pagerItems(current, pages) {
  if (pages <= 1) return [1];
  const wanted = new Set([1, pages, current, current - 1, current + 1]);
  if (current <= 3) { wanted.add(2); wanted.add(3); }
  if (current >= pages - 2) { wanted.add(pages - 1); wanted.add(pages - 2); }
  const sorted = [...wanted].filter(n => n >= 1 && n <= pages).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const n of sorted) {
    // A single hidden page reads better as the number than as an ellipsis.
    if (n - prev === 2) out.push(n - 1);
    else if (n - prev > 2) out.push('ellipsis');
    out.push(n);
    prev = n;
  }
  return out;
}

// ── rendering ───────────────────────────────────────────────────────────────

function render() {
  if (!listEl) return;
  renderMeta();

  const count = rides.length;
  if (count === 0) {
    const empty = el('div', 'list-empty', EMPTY_COPY);
    listEl.replaceChildren(empty);
    if (paginationEl) paginationEl.replaceChildren();
    return;
  }

  const pages = totalPages(count);
  if (page > pages) page = pages;
  const start = (page - 1) * PAGE_SIZE;
  const slice = rides.slice(start, start + PAGE_SIZE);

  listEl.replaceChildren(buildHead(), ...slice.map(buildRow));
  renderPagination(count, start, slice.length);
}

function renderMeta() {
  if (!metaEl) return;
  metaEl.replaceChildren();
  if (rides.length === 0) {
    metaEl.append('No rides');
    return;
  }
  const totalKm = rides.reduce((sum, r) => sum + (Number(r.distance_km) || 0), 0);
  const years = rides
    .map(r => String(r.date).slice(0, 4))
    .filter(y => /^\d{4}$/.test(y));
  // rides are newest-first, so the last year is at the tail.
  const latest = years[0];
  const earliest = years[years.length - 1];
  const range = earliest && latest
    ? (earliest === latest ? earliest : `${earliest} → ${latest}`)
    : '';

  metaEl.append(bold(fmtInt(rides.length)), ' rides · ', bold(fmtInt(Math.round(totalKm))), ' km');
  if (range) metaEl.append(' · ', bold(range));
}

function renderPagination(count, start, shown) {
  if (!paginationEl) return;
  const pages = totalPages(count);
  const from = count === 0 ? 0 : start + 1;
  const to = start + shown;

  const range = el('div', 'range');
  range.append('Showing ', bold(`${pad2(from)} → ${pad2(to)}`), ' of ', bold(fmtInt(count)), ' rides');

  const pager = el('div', 'pager');
  pager.append(pagerBtn('← PREV', { disabled: page <= 1, goto: page - 1 }));
  for (const item of pagerItems(page, pages)) {
    if (item === 'ellipsis') {
      pager.append(el('span', 'ellipsis', '…'));
    } else {
      pager.append(pagerBtn(pad2(item), { active: item === page, goto: item }));
    }
  }
  pager.append(pagerBtn('NEXT →', { disabled: page >= pages, goto: page + 1 }));

  paginationEl.replaceChildren(range, pager);
}

function buildHead() {
  const head = el('div', 'row-head');
  HEAD_LABELS.forEach((label, i) => {
    const cell = el('div', i === HEAD_LABELS.length - 1 ? 'actions-col' : null);
    if (label) cell.textContent = label;
    else cell.innerHTML = '&nbsp;';
    head.append(cell);
  });
  return head;
}

function buildRow(r) {
  const row = el('div', 'ride-row');

  const date = el('div', 'date');
  date.append(document.createTextNode(fmtRideDate(r.date)));
  if (hasTime(r.date)) {
    const t = fmtShortTime(r.date);
    if (t) date.append(el('span', 'time', t));
  }

  const title = el('div', 'title', r.title ? String(r.title) : 'Untitled ride');

  const dist = el('div', 'num');
  dist.append(document.createTextNode(fmtRideDistance(r.distance_km)), el('span', 'u', 'KM'));

  const dur = el('div', 'num', fmtDuration(r.duration_seconds));

  const speed = el('div', 'num');
  speed.append(document.createTextNode(fmtSpeed(r.speed_kmh)), el('span', 'u', 'KM/H'));

  // display:contents on desktop (cells flow into the table grid), a flex row on
  // mobile (· separated, independent of the date column) — see kadnz.css.
  const metrics = el('div', 'metrics');
  metrics.append(dist, dur, speed);

  const tag = sourceTag(r.source);
  const srcCol = el('div', 'src-col');
  srcCol.append(el('span', tag.muted ? 'src demo' : 'src', tag.label));

  const actions = el('div', 'actions-col');
  const editBtn = el('button', 'row-act', 'Edit');
  editBtn.type = 'button';
  editBtn.addEventListener('click', () => openRideEditor(r));
  actions.append(editBtn);

  row.append(date, title, metrics, srcCol, actions);
  return row;
}

// ── DOM helpers ─────────────────────────────────────────────────────────────

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function bold(text) {
  return el('b', null, text);
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function pagerBtn(label, { disabled = false, active = false, goto } = {}) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'pager-btn' + (active ? ' on' : '') + (disabled ? ' disabled' : '');
  btn.textContent = label;
  if (disabled || active) {
    btn.disabled = disabled;
  } else {
    btn.addEventListener('click', () => goTo(goto));
  }
  return btn;
}

function goTo(target) {
  const pages = totalPages(rides.length);
  const next = Math.min(Math.max(1, target), pages);
  if (next === page) return;
  page = next;
  render();
}
